import { metadataAPI } from '../api/enhancedApiService';

/**
 * Service for integrating and transforming metadata from different sources
 * Handles the complex nested API response structures and combines them into unified data
 */
class MetadataIntegrationService {

  /**
   * Fetch and integrate all metadata types for a connection
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Configuration options
   * @returns {Promise<Object>} Integrated metadata object
   */
  async getIntegratedMetadata(connectionId, options = {}) {
    const {
      includeColumns = true,
      forceFresh = false
    } = options;

    try {

      // Fetch all metadata types in parallel
      const promises = [
        metadataAPI.getMetadata(connectionId, 'tables', { forceFresh })
      ];

      if (includeColumns) {
        promises.push(metadataAPI.getMetadata(connectionId, 'columns', { forceFresh }));
      }


      const results = await Promise.allSettled(promises);

      // Extract results, handling any failures gracefully
      const tablesResult = results[0];
      const columnsResult = includeColumns ? results[1] : null;

      // Extract raw metadata from API responses
      const rawTablesMetadata = tablesResult.status === 'fulfilled' ? tablesResult.value : null;
      const rawColumnsMetadata = columnsResult?.status === 'fulfilled' ? columnsResult.value : null;

      // Transform and integrate the metadata
      const integratedMetadata = this.integrateMetadata({
        tables: rawTablesMetadata,
        columns: rawColumnsMetadata,
        statistics: null
      });


      return {
        success: true,
        data: integratedMetadata,
        errors: this.collectErrors(results),
        freshness: this.calculateFreshness([rawTablesMetadata, rawColumnsMetadata])
      };

    } catch (error) {
      console.error('[MetadataIntegration] Error fetching integrated metadata:', error);
      return {
        success: false,
        data: { tables: [], columns: [] },
        errors: [error.message],
        freshness: { status: 'error', age_seconds: null }
      };
    }
  }

  /**
   * Integrate metadata from different sources into unified structures
   * @param {Object} rawMetadata - Raw metadata from API responses
   * @returns {Object} Integrated metadata
   */
  integrateMetadata({ tables, columns, statistics }) {
    // Extract tables
    const extractedTables = this.extractTables(tables);
    const extractedColumns = this.extractColumns(columns);

    // Create lookup maps for efficient integration
    const columnsByTable = this.createColumnsByTableMap(extractedColumns);

    // Integrate tables with their columns (no statistics)
    const integratedTables = extractedTables.map(table => {
      const tableColumns = columnsByTable[table.name] || [];

      return {
        ...table,
        // Enhanced column information
        columns: tableColumns,
        column_count: tableColumns.length || table.column_count || 0
      };
    });

    // Keep columns as-is (no statistics)
    const integratedColumns = extractedColumns;

    const summaryData = {
      total_tables: integratedTables.length,
      totalTables: integratedTables.length,
      total_columns: integratedColumns.length,
      totalColumns: integratedColumns.length,
      column_types: this.summarizeColumnTypes(integratedColumns)
    };

    return {
      tables: integratedTables,
      columns: integratedColumns,
      summary: summaryData
    };
  }

  /**
   * Extract tables from API response
   * @param {Object} tablesResponse - API response containing tables
   * @returns {Array} Array of table objects
   */
  extractTables(tablesResponse) {
    if (!tablesResponse) return [];

    let extractedTables = [];

    // Handle different nesting levels in the API response
    const possiblePaths = [
      tablesResponse?.metadata?.metadata?.tables,
      tablesResponse?.metadata?.metadata?.metadata?.tables,
      tablesResponse?.metadata?.tables,
      tablesResponse?.tables,
      tablesResponse?.metadata,
      tablesResponse
    ];

    for (const path of possiblePaths) {
      if (path && Array.isArray(path)) {
        extractedTables = path;
        break;
      } else if (path && typeof path === 'object' && !Array.isArray(path)) {
        // Convert object to array if it's a key-value structure
        extractedTables = Object.keys(path).map(key => ({
          name: key,
          id: path[key].id || `table_${key}`,
          ...path[key]
        }));
        break;
      }
    }

    return extractedTables;
  }

  /**
   * Extract columns from API response
   * @param {Object} columnsResponse - API response containing columns
   * @returns {Array} Array of column objects
   */
  extractColumns(columnsResponse) {
    if (!columnsResponse) {
      console.log('[MetadataIntegration] extractColumns: No response');
      return [];
    }

    console.log('[MetadataIntegration] extractColumns: Full response structure:', {
      hasMetadata: !!columnsResponse?.metadata,
      metadataKeys: columnsResponse?.metadata ? Object.keys(columnsResponse.metadata) : [],
      topLevelKeys: Object.keys(columnsResponse)
    });

    let extractedColumns = [];

    // Handle columns_by_table structure (new format)
    const columnsByTable = columnsResponse?.metadata?.metadata?.columns_by_table ||
                          columnsResponse?.metadata?.columns_by_table ||
                          columnsResponse?.columns_by_table;

    console.log('[MetadataIntegration] extractColumns: columnsByTable found?', !!columnsByTable);

    if (columnsByTable) {
      console.log('[MetadataIntegration] extractColumns: columnsByTable has tables:', Object.keys(columnsByTable).length);
      Object.entries(columnsByTable).forEach(([tableName, columns]) => {
        if (Array.isArray(columns)) {
          columns.forEach(column => {
            extractedColumns.push({
              ...column,
              table_name: tableName
            });
          });
        }
      });
      console.log('[MetadataIntegration] extractColumns: Extracted total columns:', extractedColumns.length);
    } else {
      console.log('[MetadataIntegration] extractColumns: columns_by_table not found, trying flat structure');
      // Handle flat column structure (older format)
      const possiblePaths = [
        columnsResponse?.metadata?.metadata?.columns,
        columnsResponse?.metadata?.columns,
        columnsResponse?.columns,
        columnsResponse?.metadata,
        columnsResponse
      ];

      for (const path of possiblePaths) {
        if (path && Array.isArray(path)) {
          extractedColumns = path;
          console.log('[MetadataIntegration] extractColumns: Found flat columns array:', extractedColumns.length);
          break;
        }
      }
    }

    console.log('[MetadataIntegration] extractColumns: Final result:', extractedColumns.length, 'columns');
    return extractedColumns;
  }

  /**
   * Extract statistics from API response
   * @param {Object} statisticsResponse - API response containing statistics
   * @returns {Array} Array of statistics objects
   */
  extractStatistics(statisticsResponse) {
    if (!statisticsResponse) return [];

    let extractedStatistics = [];

    // Handle statistics_by_table structure (new format)
    const statisticsByTable = statisticsResponse?.metadata?.metadata?.statistics_by_table ||
                             statisticsResponse?.metadata?.statistics_by_table ||
                             statisticsResponse?.statistics_by_table;

    if (statisticsByTable) {
      Object.entries(statisticsByTable).forEach(([tableName, tableData]) => {
        // Process column statistics
        if (tableData.column_statistics) {
          Object.entries(tableData.column_statistics).forEach(([columnName, columnStats]) => {
            // Handle both nested and flat statistics structures
            let basicStats, numericStats, stringStats;

            if (columnStats.basic || columnStats.numeric || columnStats.string) {
              // Nested structure
              basicStats = columnStats.basic || {};
              numericStats = columnStats.numeric || {};
              stringStats = columnStats.string || {};
            } else {
              // Flat structure - all stats are at the top level
              basicStats = columnStats;
              numericStats = columnStats;
              stringStats = columnStats;
            }

            extractedStatistics.push({
              table_name: tableName,
              column_name: columnName,
              // Get data type from the column stats - your API doesn't seem to have 'type' field
              data_type: this.inferDataTypeFromStats(columnStats, columnName) || 'unknown',

              // Basic statistics - handle both nested and flat, and convert numeric booleans
              null_count: basicStats.null_count ?? columnStats.null_count ?? null,
              null_percentage: basicStats.null_percentage ?? columnStats.null_percentage ?? null,
              distinct_count: basicStats.distinct_count ?? columnStats.distinct_count ?? null,
              distinct_percentage: basicStats.distinct_percentage ?? columnStats.distinct_percentage ?? null,
              is_unique: this.convertNumericBoolean(basicStats.is_unique ?? columnStats.is_unique),

              // Table-level information
              row_count: tableData.general?.row_count || tableData.row_count || 0,

              // Type-specific statistics
              min: numericStats.min_value || numericStats.min || stringStats.min_length || columnStats.min_value || columnStats.min,
              max: numericStats.max_value || numericStats.max || stringStats.max_length || columnStats.max_value || columnStats.max,
              avg: numericStats.avg_value || numericStats.avg || stringStats.avg_length || columnStats.avg_value || columnStats.avg,

              // Additional numeric stats
              sum: numericStats.sum || columnStats.sum,
              stddev: numericStats.stddev || columnStats.stddev,

              // Additional string stats
              empty_count: stringStats.empty_count || columnStats.empty_count,

              // Metadata
              collected_at: tableData.collected_at
            });
          });
        }
      });
    } else {
      // Handle flat statistics structure (older format)
      const possiblePaths = [
        statisticsResponse?.metadata?.metadata?.statistics,
        statisticsResponse?.metadata?.statistics,
        statisticsResponse?.statistics,
        statisticsResponse?.metadata,
        statisticsResponse
      ];

      for (const path of possiblePaths) {
        if (path && Array.isArray(path)) {
          extractedStatistics = path;
          break;
        }
      }
    }

    console.log('[MetadataIntegration] Extracted statistics:', extractedStatistics.length);
    return extractedStatistics;
  }

  /**
   * Extract table-level statistics from metadata status API response
   * @param {Object} metadataStatusResponse - Metadata status API response (same as Data Explorer uses)
   * @returns {Object} Map of table name to table statistics
   */
  extractTableStatistics(metadataStatusResponse) {
    if (!metadataStatusResponse) {
      console.log('[MetadataIntegration] No metadata status response provided to extractTableStatistics');
      return {};
    }

    console.log('[MetadataIntegration] Extracting table statistics from metadata status:', metadataStatusResponse);
    const tableStatistics = {};

    // Extract from metadata status response (like Data Explorer does)
    const statusData = metadataStatusResponse?.data || metadataStatusResponse;
    
    // Check for tables with statistics in the status response
    const tablesWithStats = statusData?.tables_metadata || statusData?.tables || [];
    
    console.log('[MetadataIntegration] Found tables with stats:', tablesWithStats);
    
    if (Array.isArray(tablesWithStats)) {
      tablesWithStats.forEach((tableData, index) => {
        const tableName = tableData.name || tableData.table_name || `table_${index}`;
        console.log(`[MetadataIntegration] Processing table ${tableName}:`, tableData);
        
        const extractedStats = {
          // Extract row count from various possible locations
          row_count: tableData.row_count ?? tableData.rows ?? tableData.record_count ?? null,
          column_count: tableData.column_count ?? tableData.columns?.length ?? 0,
          health_score: tableData.health_score ?? 65,
          has_primary_key: tableData.has_primary_key ?? false,
          primary_keys: tableData.primary_keys || tableData.primary_key || [],
          column_type_distribution: tableData.column_type_distribution ?? {},
          nullable_columns: tableData.nullable_columns ?? 0,
          non_nullable_columns: tableData.non_nullable_columns ?? 0,
          collected_at: tableData.collected_at ?? tableData.last_updated ?? null
        };
        console.log(`[MetadataIntegration] Extracted stats for ${tableName}:`, extractedStats);
        tableStatistics[tableName] = extractedStats;
      });
    }

    // Also check if the status response has table statistics in a different format
    if (statusData?.statistics_by_table) {
      console.log('[MetadataIntegration] Also found statistics_by_table in status response');
      Object.entries(statusData.statistics_by_table).forEach(([tableName, tableData]) => {
        if (!tableStatistics[tableName]) {
          // Only add if we don't already have stats for this table
          const extractedStats = {
            row_count: tableData.general?.row_count ?? tableData.row_count ?? null,
            column_count: tableData.column_count ?? 0,
            health_score: tableData.health_score ?? 65,
            has_primary_key: tableData.has_primary_key ?? false,
            primary_keys: tableData.primary_keys ?? [],
            column_type_distribution: tableData.column_type_distribution ?? {},
            nullable_columns: tableData.nullable_columns ?? 0,
            non_nullable_columns: tableData.non_nullable_columns ?? 0,
            collected_at: tableData.collected_at ?? null
          };
          tableStatistics[tableName] = extractedStats;
        }
      });
    }

    console.log('[MetadataIntegration] Final extracted table statistics:', tableStatistics);
    return tableStatistics;
  }

  /**
   * Create a map of columns grouped by table name
   * @param {Array} columns - Array of column objects
   * @returns {Object} Map of table name to columns array
   */
  createColumnsByTableMap(columns) {
    const columnsByTable = {};

    columns.forEach(column => {
      const tableName = column.table_name;
      if (!columnsByTable[tableName]) {
        columnsByTable[tableName] = [];
      }
      columnsByTable[tableName].push(column);
    });

    return columnsByTable;
  }

  /**
   * Convert numeric boolean values (1.0/0.0) to actual booleans
   * @param {any} value - Value to convert
   * @returns {boolean|null} Boolean value or null if undefined
   */
  convertNumericBoolean(value) {
    if (value === null || value === undefined) return null;
    if (value === 1 || value === 1.0 || value === true) return true;
    if (value === 0 || value === 0.0 || value === false) return false;
    return null;
  }

  /**
   * Infer data type from statistics when type field is not available
   * @param {Object} columnStats - Column statistics object
   * @param {string} columnName - Column name for pattern matching
   * @returns {string} Inferred data type
   */
  inferDataTypeFromStats(columnStats, columnName) {
    // Check if we have explicit type field
    if (columnStats.type) {
      return columnStats.type;
    }

    // Look for numeric indicators
    if (columnStats.avg_value !== undefined || columnStats.min_value !== undefined || columnStats.sum !== undefined) {
      // Looks like numeric data
      if (columnName.toLowerCase().includes('key') || columnName.toLowerCase().includes('id')) {
        return 'DECIMAL(38, 0)'; // Likely an ID field
      }
      return 'DECIMAL'; // Generic decimal
    }

    // Look for string indicators
    if (columnStats.avg_length !== undefined || columnStats.min_length !== undefined) {
      const maxLen = columnStats.max_length;
      if (maxLen && maxLen <= 50) {
        return `VARCHAR(${maxLen})`;
      }
      return 'VARCHAR'; // Generic varchar
    }

    // Look for date indicators
    if (columnStats.min_date !== undefined || columnStats.max_date !== undefined) {
      return 'DATE';
    }

    // Pattern matching on column names
    const lowerName = columnName.toLowerCase();
    if (lowerName.includes('date') || lowerName.includes('time')) {
      return 'DATE';
    }
    if (lowerName.includes('comment') || lowerName.includes('description') || lowerName.includes('name')) {
      return 'VARCHAR';
    }
    if (lowerName.includes('key') || lowerName.includes('id') || lowerName.includes('number')) {
      return 'DECIMAL';
    }

    return 'unknown';
  }

  /**
   * Categorize data type into broader categories
   * @param {string} dataType - Specific data type
   * @returns {string} Category name
   */
  categorizeDataType(dataType) {
    const type = dataType.toLowerCase();

    if (type.includes('varchar') || type.includes('text') || type.includes('char')) {
      return 'text';
    } else if (type.includes('decimal') || type.includes('numeric') || type.includes('int') || type.includes('float')) {
      return 'numeric';
    } else if (type.includes('date') || type.includes('time')) {
      return 'datetime';
    } else if (type.includes('bool')) {
      return 'boolean';
    } else {
      return 'other';
    }
  }

  /**
   * Find the largest table by row count
   * @param {Array} tables - Array of table objects
   * @returns {Object|null} Largest table or null if none found
   */
  findLargestTable(tables) {
    if (!tables || tables.length === 0) return null;

    return tables.reduce((largest, current) => {
      const currentRowCount = typeof current.row_count === 'number' ? current.row_count : -1;
      const largestRowCount = typeof largest.row_count === 'number' ? largest.row_count : -1;
      return currentRowCount > largestRowCount ? current : largest;
    });
  }

  /**
   * Summarize column types across all tables
   * @param {Array} columns - Array of column objects
   * @returns {Object} Summary of column types
   */
  summarizeColumnTypes(columns) {
    const typeSummary = {};

    columns.forEach(column => {
      const type = column.type || column.data_type || 'unknown';
      const category = this.categorizeDataType(type);
      typeSummary[category] = (typeSummary[category] || 0) + 1;
    });

    return typeSummary;
  }

  /**
   * Collect errors from Promise.allSettled results
   * @param {Array} results - Results from Promise.allSettled
   * @returns {Array} Array of error messages
   */
  collectErrors(results) {
    return results
      .filter(result => result.status === 'rejected')
      .map(result => result.reason?.message || 'Unknown error');
  }

  /**
   * Calculate overall freshness from multiple metadata sources
   * @param {Array} metadataResponses - Array of metadata responses
   * @returns {Object} Freshness information
   */
  calculateFreshness(metadataResponses) {
    const validResponses = metadataResponses.filter(Boolean);
    if (validResponses.length === 0) {
      return { status: 'unknown', age_seconds: null };
    }

    // Find the oldest freshness status
    let oldestAgeSeconds = 0;
    let worstStatus = 'fresh';

    validResponses.forEach(response => {
      const freshness = response?.metadata?.freshness || response?.freshness;
      if (freshness) {
        const ageSeconds = freshness.age_seconds || 0;
        const status = freshness.status || 'unknown';

        if (ageSeconds > oldestAgeSeconds) {
          oldestAgeSeconds = ageSeconds;
        }

        // Determine worst status (unknown > stale > recent > fresh)
        const statusPriority = { fresh: 0, recent: 1, stale: 2, unknown: 3, error: 4 };
        if (statusPriority[status] > statusPriority[worstStatus]) {
          worstStatus = status;
        }
      }
    });

    return {
      status: worstStatus,
      age_seconds: oldestAgeSeconds
    };
  }

  /**
   * Get enhanced table information with all integrated data
   * @param {string} connectionId - Database connection ID
   * @param {string} tableName - Name of the table
   * @returns {Promise<Object>} Enhanced table information
   */
  async getEnhancedTableInfo(connectionId, tableName) {
    try {
      const integrated = await this.getIntegratedMetadata(connectionId);

      if (!integrated.success) {
        throw new Error('Failed to get integrated metadata');
      }

      // Match against fully qualified name (database.schema.table)
      const table = integrated.data.tables.find(t => {
        const fullName = t.database && t.schema && t.name
          ? `${t.database}.${t.schema}.${t.name}`
          : t.name;
        return fullName === tableName || t.name === tableName;
      });
      if (!table) {
        throw new Error(`Table ${tableName} not found`);
      }

      // Match columns and statistics against qualified name
      const tableColumns = integrated.data.columns.filter(c => {
        const columnTableFull = c.database && c.schema && c.table_name
          ? `${c.database}.${c.schema}.${c.table_name}`
          : c.table_name;
        return columnTableFull === tableName || c.table_name === tableName;
      });
      const tableStatistics = integrated.data.statistics.filter(s => {
        const statTableFull = s.database && s.schema && s.table_name
          ? `${s.database}.${s.schema}.${s.table_name}`
          : s.table_name;
        return statTableFull === tableName || s.table_name === tableName;
      });

      return {
        table,
        columns: tableColumns,
        statistics: tableStatistics,
        summary: {
          total_columns: tableColumns.length,
          nullable_columns: tableColumns.filter(c => c.nullable).length,
          unique_columns: tableStatistics.filter(s => s.is_unique).length,
          columns_with_nulls: tableStatistics.filter(s => s.null_count > 0).length
        }
      };
    } catch (error) {
      console.error(`[MetadataIntegration] Error getting enhanced table info for ${tableName}:`, error);
      throw error;
    }
  }

  // =========================================================================
  // TIER-AWARE METADATA METHODS
  // New methods that leverage the tiered metadata system
  // =========================================================================

  /**
   * Get table metadata with tier awareness
   * Uses the new tiered metadata system (table_metadata_cache)
   * @param {string} connectionId - Database connection ID
   * @param {Object} filters - Optional filters (schema, table_name, include_inactive)
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Promise resolving to tables and tier status
   */
  async getTableMetadataTiered(connectionId, filters = {}, options = {}) {
    try {
      // Get table metadata and tier status in parallel
      const [tablesResponse, tierStatusResponse] = await Promise.allSettled([
        metadataAPI.getTableMetadataTiered(connectionId, filters, options),
        metadataAPI.getTierStatus(connectionId, options)
      ]);

      const tablesResult = tablesResponse.status === 'fulfilled' ? tablesResponse.value : null;
      const tierResult = tierStatusResponse.status === 'fulfilled' ? tierStatusResponse.value : null;

      // Log tier status errors for debugging
      if (tierStatusResponse.status === 'rejected') {
        console.error('[MetadataIntegration] Tier status fetch failed:', tierStatusResponse.reason);
      }

      if (!tablesResult || !tablesResult.success) {
        throw new Error('Failed to retrieve table metadata');
      }

      // Collect all errors from both promises
      const errors = [];
      if (tablesResponse.status === 'rejected') {
        errors.push(tablesResponse.reason);
      }
      if (tierStatusResponse.status === 'rejected') {
        errors.push(tierStatusResponse.reason);
      }

      return {
        success: true,
        tables: tablesResult.tables || [],
        count: tablesResult.count || 0,
        tierStatus: tierResult,
        errors
      };
    } catch (error) {
      console.error('[MetadataIntegration] Error getting tiered table metadata:', error);
      return {
        success: false,
        tables: [],
        count: 0,
        tierStatus: null,
        errors: [error.message]
      };
    }
  }

  /**
   * Get schemas with tier awareness
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Promise resolving to schemas and tier status
   */
  async getSchemasTiered(connectionId, options = {}) {
    try {
      // Get schemas and tier status in parallel
      const [schemasResponse, tierStatus] = await Promise.allSettled([
        metadataAPI.getSchemaList(connectionId, options),
        metadataAPI.getTierStatus(connectionId, options)
      ]);

      const schemasResult = schemasResponse.status === 'fulfilled' ? schemasResponse.value : null;
      const tierResult = tierStatus.status === 'fulfilled' ? tierStatus.value : null;

      if (!schemasResult || !schemasResult.success) {
        throw new Error('Failed to retrieve schemas');
      }

      return {
        success: true,
        schemas: schemasResult.schemas || [],
        count: schemasResult.count || 0,
        tierStatus: tierResult,
        errors: schemasResponse.status === 'rejected' ? [schemasResponse.reason] : []
      };
    } catch (error) {
      console.error('[MetadataIntegration] Error getting schemas:', error);
      return {
        success: false,
        schemas: [],
        count: 0,
        tierStatus: null,
        errors: [error.message]
      };
    }
  }

  /**
   * Get schema summary with tier awareness
   * @param {string} connectionId - Database connection ID
   * @param {string} schema - Schema name
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Promise resolving to schema summary and tier status
   */
  async getSchemaSummaryTiered(connectionId, schema, options = {}) {
    try {
      // Get schema summary and tier status in parallel
      const [summaryResponse, tierStatus] = await Promise.allSettled([
        metadataAPI.getSchemaMetadata(connectionId, schema, options),
        metadataAPI.getTierStatus(connectionId, options)
      ]);

      const summaryResult = summaryResponse.status === 'fulfilled' ? summaryResponse.value : null;
      const tierResult = tierStatus.status === 'fulfilled' ? tierStatus.value : null;

      if (!summaryResult || !summaryResult.success) {
        throw new Error('Failed to retrieve schema summary');
      }

      return {
        success: true,
        schema: summaryResult.schema,
        summary: summaryResult.summary,
        tierStatus: tierResult,
        errors: summaryResponse.status === 'rejected' ? [summaryResponse.reason] : []
      };
    } catch (error) {
      console.error('[MetadataIntegration] Error getting schema summary:', error);
      return {
        success: false,
        schema: null,
        summary: {},
        tierStatus: null,
        errors: [error.message]
      };
    }
  }

  /**
   * Get connection statistics with tier awareness
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Promise resolving to connection statistics and tier status
   */
  async getConnectionStatisticsTiered(connectionId, options = {}) {
    try {
      // Get statistics and tier status in parallel
      const [statsResponse, tierStatus] = await Promise.allSettled([
        metadataAPI.getConnectionStatistics(connectionId, options),
        metadataAPI.getTierStatus(connectionId, options)
      ]);

      const statsResult = statsResponse.status === 'fulfilled' ? statsResponse.value : null;
      const tierResult = tierStatus.status === 'fulfilled' ? tierStatus.value : null;

      if (!statsResult || !statsResult.success) {
        throw new Error('Failed to retrieve connection statistics');
      }

      return {
        success: true,
        statistics: statsResult.statistics,
        tierStatus: tierResult,
        errors: statsResponse.status === 'rejected' ? [statsResponse.reason] : []
      };
    } catch (error) {
      console.error('[MetadataIntegration] Error getting connection statistics:', error);
      return {
        success: false,
        statistics: {},
        tierStatus: null,
        errors: [error.message]
      };
    }
  }

  /**
   * Get metadata with tier status integration
   * Enhanced version of getIntegratedMetadata that includes tier status
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Configuration options
   * @returns {Promise<Object>} Integrated metadata with tier status
   */
  async getIntegratedMetadataWithTiers(connectionId, options = {}) {
    try {
      // Get integrated metadata and tier status in parallel
      const [integratedResponse, tierStatus] = await Promise.allSettled([
        this.getIntegratedMetadata(connectionId, options),
        metadataAPI.getTierStatus(connectionId, options)
      ]);

      const integratedResult = integratedResponse.status === 'fulfilled' ? integratedResponse.value : null;
      const tierResult = tierStatus.status === 'fulfilled' ? tierStatus.value : null;

      if (!integratedResult || !integratedResult.success) {
        throw new Error('Failed to get integrated metadata');
      }

      return {
        ...integratedResult,
        tierStatus: tierResult
      };
    } catch (error) {
      console.error('[MetadataIntegration] Error getting integrated metadata with tiers:', error);
      return {
        success: false,
        data: { tables: [], columns: [], statistics: [] },
        errors: [error.message],
        freshness: { status: 'error', age_seconds: null },
        tierStatus: null
      };
    }
  }
}

// Export singleton instance
export const metadataIntegrationService = new MetadataIntegrationService();
export default metadataIntegrationService;